// „Кога се разделят маси" — по час за един ден, по ден за по-дълъг период.
//
// Досегашната графика беше нарисувана с inline стилове и с JS hover, който
// „залепва" на телефон. Тук всичко е Tailwind, а подсказката е `title` на
// самото стълбче. Единствените inline стойности са ВИСОЧИНИТЕ — те идват от
// данните и не могат да са класове.

import { ClockCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { activityLabelStep, type ActivityPoint } from "./billsSplitsMath";

/** Височина на зоната със стълбчетата. */
const PLOT_HEIGHT = 140;
/** Най-ниското видимо стълбче — час с разделяне не бива да изчезва. */
const MIN_BAR_HEIGHT = 2;

type Props = {
  points: ActivityPoint[];
  /** Периодът е един ден — стълбчетата са часове. */
  isHourly: boolean;
  isNarrow: boolean;
};

export const BillsSplitsActivityCard = ({
  points,
  isHourly,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();

  const max = Math.max(1, ...points.map((point) => point.count));
  const total = points.reduce((sum, point) => sum + point.count, 0);
  const hasData = total > 0;
  const labelStep = activityLabelStep(points.length, isHourly);

  // Шейсет дневни стълбчета не се събират на телефон. Графиката скролва в
  // СВОЯ контейнер — страницата не бива да скролва настрани заради нея.
  const minWidth =
    points.length > 24
      ? "min-w-[720px]"
      : isNarrow && points.length > 12
        ? "min-w-[420px]"
        : "";

  return (
    <section className="rounded-xl border border-shade-2 bg-surface px-4 py-3.5">
      <header className="mb-3 flex items-center gap-2.5">
        <span className="grid size-8 shrink-0 place-items-center rounded-[9px] bg-brand-tint">
          <ClockCircleOutlined className="text-[15px] text-brand-deep-ink" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
            {t("reportsPage.billsSplitsActivityKicker")}
          </p>
          <h2 className="mt-1 truncate text-[15px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
            {isHourly
              ? t("reportsPage.billsSplitsActivityHourly")
              : t("reportsPage.billsSplitsActivityDaily")}
          </h2>
        </div>
        {hasData ? (
          <span className="shrink-0 font-pos-mono text-[12px] font-semibold tabular-nums text-muted">
            {isHourly ? `${total} · 00–23` : total}
          </span>
        ) : null}
      </header>

      {hasData ? (
        <div className="-mx-1 overflow-x-auto px-1">
          <div className={minWidth}>
            <div className="relative flex h-[140px] items-end gap-[3px]">
              {/* Помощните линии стоят на четвъртинки от височината —
                  фиксирани дялове, затова са класове, не изчисление. */}
              {["top-0", "top-1/4", "top-2/4", "top-3/4"].map((offset) => (
                <span
                  key={offset}
                  aria-hidden
                  className={`pointer-events-none absolute inset-x-0 border-t border-dashed border-shade-2 ${offset}`}
                />
              ))}

              {points.map((point) => (
                <span
                  key={point.key}
                  title={`${
                    isHourly ? `${point.label}:00` : point.label
                  } · ${point.count}`}
                  className="flex h-full flex-1 items-end"
                >
                  <span
                    className={`w-full rounded-t-[4px] transition-opacity hover:opacity-80 ${
                      point.count > 0 ? "bg-brand" : "bg-fill"
                    }`}
                    /* Височината идва от данните — известна е чак по време на
                       изпълнение и не може да бъде клас. */
                    style={{
                      height:
                        point.count > 0
                          ? Math.max(
                              MIN_BAR_HEIGHT,
                              (point.count / max) * PLOT_HEIGHT,
                            )
                          : MIN_BAR_HEIGHT,
                    }}
                  />
                </span>
              ))}
            </div>

            <div className="mt-1.5 flex gap-[3px]">
              {points.map((point, index) => (
                <span
                  key={`label-${point.key}`}
                  aria-hidden={index % labelStep !== 0}
                  className={`flex-1 whitespace-nowrap text-center font-pos-mono text-[10px] font-semibold leading-none text-muted ${
                    index % labelStep === 0 ? "" : "invisible"
                  }`}
                >
                  {point.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className="py-10 text-center text-[12.5px] font-medium text-muted">
          {t("reportsPage.billsSplitsActivityEmpty")}
        </p>
      )}
    </section>
  );
};
